import { ChangeEvent, useRef, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Checkbox,
  HStack,
  Heading,
  Icon,
  Input,
  Label,
  NewButton,
  Paragraph,
  Separator,
  Text,
  VStack,
  useToast,
} from '@eluve/components';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { getSourceTypeLabel } from './citation';
import { UploadedFile } from './types';
import { useUploadPdf } from './usePdfUpload';

interface KnowledgeSourcesProps {
  tenantId: string;
  initialSources: string[];
  initialFiles: UploadedFile[];
  onSave: (sources: string[]) => Promise<void>;
  onSaveFiles: (files: UploadedFile[]) => Promise<void>;
}

const AVAILABLE_SOURCES = ['unsignedNotes'];

export const KnowledgeSources = ({
  tenantId,
  initialSources,
  initialFiles,
  onSave,
  onSaveFiles,
}: KnowledgeSourcesProps) => {
  const [sources, setSources] = useState<string[]>(initialSources);
  const [files, setFiles] = useState<UploadedFile[]>(initialFiles);
  const [isSaving, setIsSaving] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const userId = useUserIdFromSession();
  const apiClient = useApiClient();
  const toast = useToast();

  const handleToggle = async (source: string) => {
    const newSources = sources.includes(source)
      ? sources.filter((s) => s !== source)
      : [...sources, source];
    setIsSaving(true);
    try {
      await onSave(newSources);
      setSources(newSources);
    } catch {
      toast.error('Failed to update knowledge sources');
      setSources(sources);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUploadComplete = async (file: UploadedFile) => {
    const newFiles = [...files, file];
    setFiles(newFiles);

    setIsSaving(true);
    try {
      await onSaveFiles(newFiles);
    } catch {
      toast.error('Failed to save uploaded file');
      setFiles(files);
    } finally {
      setIsSaving(false);
    }
  };

  const { uploadFile, isUploading } = useUploadPdf({
    tenantId,
    onUploadComplete: handleFileUploadComplete,
  });

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
    event.target.value = '';
  };

  const handleRemoveFile = async (fileId: string) => {
    const newFiles = files.filter((f) => f.id !== fileId);
    setFiles(newFiles);

    setIsSaving(true);
    try {
      await onSaveFiles(newFiles);
    } catch {
      toast.error('Failed to remove file');
      setFiles(files);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadFile = async (file: UploadedFile) => {
    try {
      setDownloadingFileId(file.id);

      if (file.fileUrl) {
        window.open(file.fileUrl, '_blank');
        return;
      }

      const getFileResponse = await apiClient.users.getUserFileSignedUrl({
        params: { tenantId, userId, fileId: file.id },
      });

      if (getFileResponse.status !== 200) {
        throw new Error('Failed to get file download URL');
      }

      const presignedUrl = getFileResponse.body.presignedUrl;
      if (!presignedUrl) {
        throw new Error('File URL not available');
      }

      // Open file in new tab
      window.open(presignedUrl, '_blank');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to download file';
      toast.error(errorMessage);
    } finally {
      setDownloadingFileId(null);
    }
  };
  return (
    <div className="w-full rounded-lg border border-border-primary bg-bg-secondary p-6">
      <VStack gap={4} className="w-full">
        <VStack gap={1}>
          <Heading size="md" weight="semibold">
            Knowledge
          </Heading>
          <Paragraph size="sm" color="secondary">
            Select which knowledge sources to include in chat context
          </Paragraph>
        </VStack>
        <VStack gap={3} className="w-full">
          {AVAILABLE_SOURCES.map((source) => (
            <HStack key={source} gap={3} className="items-center">
              <Checkbox
                checked={sources.includes(source)}
                onCheckedChange={() => handleToggle(source)}
                disabled={isSaving}
              />
              <Label className="flex cursor-pointer gap-3">
                <Text size="sm">{getSourceTypeLabel(source)}</Text>
              </Label>
            </HStack>
          ))}
        </VStack>

        <Separator />

        <VStack gap={2} className="w-full">
          <HStack className="w-full justify-between">
            <Text weight="semibold" size="sm">
              Uploaded Files
            </Text>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              disabled={isUploading}
              className="hidden"
            />
            <NewButton
              text="Upload files"
              type="outline"
              size="s"
              icon={{ name: 'Upload', position: 'left' }}
              onClick={handleFileUploadClick}
              disabled={isSaving || isUploading}
              loading={isUploading}
            />
          </HStack>

          {files.length === 0 ? (
            <Paragraph size="sm" color="tertiary">
              No files uploaded yet
            </Paragraph>
          ) : (
            <VStack gap={2} className="w-full">
              {files.map((file) => {
                const isDownloading = downloadingFileId === file.id;
                return (
                  <HStack
                    key={file.id}
                    className="w-full cursor-pointer items-center justify-between rounded border border-border-primary bg-bg-primary p-2 hover:bg-bg-secondary"
                    onClick={() => !isDownloading && handleDownloadFile(file)}
                  >
                    <HStack gap={2} className="items-center">
                      {isDownloading ? (
                        <Icon
                          name="LoaderCircle"
                          size="sm"
                          className="animate-spin"
                        />
                      ) : (
                        <Icon name="FileText" size="sm" />
                      )}
                      <Text size="sm" className="truncate">
                        {file.fileName}
                      </Text>
                    </HStack>
                    <NewButton
                      icon={{ name: 'X' }}
                      type="ghost"
                      size="s"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(file.id);
                      }}
                      disabled={isSaving || isDownloading}
                    />
                  </HStack>
                );
              })}
            </VStack>
          )}
        </VStack>
      </VStack>
    </div>
  );
};
